import { Component, OnInit, OnDestroy } from '@angular/core';
import { ConnectionService } from '../../services/Connection/connection.service';
import { Company } from '../../Interfaces/company';
import { Subscription, interval } from 'rxjs';

@Component({
  selector: 'app-banners',
  templateUrl: './banners.component.html',
  styleUrl: './banners.component.css'
})
export class BannersComponent implements OnInit, OnDestroy {
  company!: Company;
  banners: any[] = [];
  
  // Clone logic for infinite loop
  extendedBanners: any[] = [];
  currentIndex = 1; // Start at 1 because 0 is a clone
  transitionDuration = 600; // ms
  isTransitioning = true;
  
  autoPlaySubscription?: Subscription;
  autoPlayInterval = 5000;

  // Swipe logic
  touchStartX = 0;
  touchEndX = 0;
  minSwipeDistance = 50;

  constructor(private connection: ConnectionService) {}

  ngOnInit() {
    this.connection.company$.subscribe((company) => {
      this.company = company;

      if (this.company?.configs?.warnings && this.company.configs.warnings.length > 0) {
        this.banners = this.company.configs.warnings;
      }
      this.setupInfiniteLoop();
    });
    
    // Initial setup if subscription is slow
    this.setupInfiniteLoop();
    this.startAutoPlay();
  }

  setupInfiniteLoop() {
    if (this.banners.length > 1) {
      // Create [Last, ...Originals, First] structure
      const firstClone = { ...this.banners[0] };
      const lastClone = { ...this.banners[this.banners.length - 1] };
      this.extendedBanners = [lastClone, ...this.banners, firstClone];
      this.currentIndex = 1;
      this.isTransitioning = false; // Disable transition for initial setup
      setTimeout(() => this.isTransitioning = true, 50);
    } else {
      // Single banner case: just show it without clones
      this.extendedBanners = [...this.banners];
      this.currentIndex = 0;
    }
  }

  ngOnDestroy() {
    this.stopAutoPlay();
  }

  // --- Carousel Logic ---

  startAutoPlay() {
    this.stopAutoPlay();
    if (this.banners.length <= 1) return; // Don't autoplay if single banner

    this.autoPlaySubscription = interval(this.autoPlayInterval).subscribe(() => {
      this.nextSlide();
    });
  }

  stopAutoPlay() {
    if (this.autoPlaySubscription) {
      this.autoPlaySubscription.unsubscribe();
    }
  }

  nextSlide() {
    if (this.banners.length <= 1) return;
    if (this.currentIndex >= this.extendedBanners.length - 1) return;
    
    this.currentIndex++;
    this.isTransitioning = true;
    
    // If reached the clone at the end, jump to real start silently
    if (this.currentIndex === this.extendedBanners.length - 1) {
      setTimeout(() => {
        this.isTransitioning = false;
        this.currentIndex = 1;
      }, this.transitionDuration);
    }
  }

  prevSlide() {
    if (this.banners.length <= 1) return;
    if (this.currentIndex <= 0) return;

    this.currentIndex--;
    this.isTransitioning = true;

    // If reached the clone at the start, jump to real end silently
    if (this.currentIndex === 0) {
      setTimeout(() => {
        this.isTransitioning = false;
        this.currentIndex = this.extendedBanners.length - 2;
      }, this.transitionDuration);
    }
  }

  goToSlide(realIndex: number) {
    if (this.banners.length <= 1) return;
    this.currentIndex = realIndex + 1; // Map real index (0-based) to extended index (1-based)
    this.isTransitioning = true;
    this.resetTimer();
  }

  resetTimer() {
    this.stopAutoPlay();
    this.startAutoPlay();
  }

  // --- Modal Logic ---
  selectedBanner: any = null;

  checkAndOpenBanner(banner: any) {
    if (banner) {
      this.selectedBanner = banner;
      this.stopAutoPlay(); // Pause carousel while reading
    }
  }

  closeModal() {
    this.selectedBanner = null;
    this.startAutoPlay(); // Resume carousel
  }

  // --- Swipe Gestures ---

  onTouchStart(event: TouchEvent) {
    if (this.banners.length <= 1) return;
    this.touchStartX = event.touches[0].clientX;
    this.stopAutoPlay(); // Pause while interacting
  }

  onTouchEnd(event: TouchEvent) {
    if (this.banners.length <= 1) return;
    this.touchEndX = event.changedTouches[0].clientX;
    this.handleSwipe();
    this.startAutoPlay(); // Resume
  }

  handleSwipe() {
    const swipeDistance = this.touchEndX - this.touchStartX;
    
    if (Math.abs(swipeDistance) > this.minSwipeDistance) {
      if (swipeDistance < 0) {
        this.nextSlide(); // Swipe Left -> Next
      } else {
        this.prevSlide(); // Swipe Right -> Prev
      }
    }
  }

  // Helper to get active dot index
  get activeDotIndex(): number {
    if (this.currentIndex === 0) return this.banners.length - 1;
    if (this.currentIndex === this.extendedBanners.length - 1) return 0;
    return this.currentIndex - 1;
  }
}
